import PropTypes from 'prop-types';
import React, { Fragment, memo, useCallback, useRef, useState } from 'react';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { AxisBottom } from '@visx/axis';
import { Group } from '@visx/group';
import { BarGroup, BarRounded, Bar } from '@visx/shape';
import { StyledBarGroupChartWrapper } from './BarGroupChart.styles';
import { withTheme } from 'styled-components';
import { Text } from '@visx/text';
import BarGroupChartLegend from './BarGroupChartLegend';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import { useWindowSize, FormatUtils } from '../../utils';
import uniqid from 'uniqid';
import BarGroupChartImage from '../../components/ChartsImages/BarGroupChartImage';

const defaultMargin = { top: 40, left: 0, right: 0, bottom: 120 };

const BarGroupChart = ({
  data,
  margin = defaultMargin,
  xValueKey,
  xLabelKey,
  xImageKey,
  keys,
  colors,
  legend,
  padding = 15,
  emptyDataMessage,
  areBarsRounded
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const windowSize = useWindowSize();
  const chartId = useRef(uniqid());

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const getXValue = (d) => d[xValueKey];
  const xScale = scaleBand({
    domain: (data && data.map(getXValue)) || [],
    padding: 0.6
  });

  const keysScale = scaleBand({
    domain: keys,
    padding: 0.0
  });
  const valueScale = scaleLinear({
    domain: [
      0,
      Math.max(
        ...((data &&
          data.map((d) => Math.max(...keys.map((key) => Number(d[key]))))) ||
          [])
      )
    ]
  });
  const colorScale = scaleOrdinal({
    domain: keys,
    range: colors || ['#D8D7EA']
  });

  xScale.rangeRound([0, xMax]);
  keysScale.rangeRound([0, xScale.bandwidth()]);
  valueScale.range([yMax, 0]);

  const getTickComponent = useCallback((d) => {
    return (
      <Fragment>
        <BarGroupChartImage
          pic={d.pic}
          x={d.x}
          y={d.y}
          width={36}
          height={36}
          parentId={chartId.current}
          initials={d.label.substring(0, 2)}
        />
        <Text
          width={80}
          height={80}
          x={d.x}
          y={d.y + 25}
          fontSize='10px'
          fontWeight='bold'
          textAnchor='middle'
          verticalAnchor='start'
        >
          {FormatUtils.capitalizeString(d.label)}
        </Text>
      </Fragment>
    );
  }, []);

  const getTickLabel = useCallback(
    (d) => {
      const item = data.find((i) => i[xValueKey] === d);
      return (item && item[xLabelKey]) || '';
    },
    [data, xValueKey, xLabelKey]
  );

  const getTickImage = useCallback(
    (d) => {
      const item = data.find((i) => i[xValueKey] === d);
      return (item && item[xImageKey]) || '';
    },
    [data, xValueKey, xImageKey]
  );

  const getGroupContent = useCallback((group, groups) => {
    const options = groups.tickLabelProps(group.value);
    const props = {
      ...options,
      x: group.to.x,
      y: 10 + group.to.y
    };

    return groups.tickComponent(props);
  }, []);

  if (!data || data.length === 0) {
    return (
      <StyledBarGroupChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledBarGroupChartWrapper>
    );
  }

  return (
    <StyledBarGroupChartWrapper ref={chartRef}>
      <svg width={width} height={height}>
        <rect rx={14} width={width} height={height} fill='#ffffff' />
        <Group
          key={`group-${chartId.current}`}
          top={margin.top}
          left={margin.left}
        >
          <BarGroup
            data={data}
            keys={keys}
            height={yMax}
            x0={getXValue}
            x0Scale={xScale}
            x1Scale={keysScale}
            yScale={valueScale}
            color={colorScale}
          >
            {(barGroups) =>
              barGroups.map((barGroup, index) => (
                <Group
                  key={`bar-group-${chartId.current}-${barGroup.index}`}
                  left={barGroup.x0}
                >
                  {barGroup.bars.map((bar) => (
                    <Fragment
                      key={`bar-group-bar-${chartId.current}-${barGroup.index}-${bar.index}`}
                    >
                      {areBarsRounded && (
                        <BarRounded
                          x={bar.x}
                          y={bar.y}
                          width={12}
                          height={bar.height}
                          radius={bar.width / 2}
                          all
                          fill={bar.color}
                        />
                      )}
                      {!areBarsRounded && (
                        <Bar
                          x={bar.x}
                          y={bar.y}
                          width={bar.width}
                          height={bar.height}
                          fill={bar.color}
                        />
                      )}
                    </Fragment>
                  ))}
                </Group>
              ))
            }
          </BarGroup>
        </Group>
        <AxisBottom
          top={yMax + margin.top + 10}
          left={margin.left}
          scale={xScale}
          hideAxisLine
          hideTicks
          tickLabelProps={(d) => ({
            fontSize: '11px',
            pic: getTickImage(d),
            label: getTickLabel(d)
          })}
          tickComponent={(d) => getTickComponent(d)}
        >
          {(axisGroups) =>
            axisGroups.ticks.map((group, index) => (
              <Group key={`axis-group-${chartId.current}-${index}`}>
                {getGroupContent(group, axisGroups)}
              </Group>
            ))
          }
        </AxisBottom>
      </svg>
      {legend && <BarGroupChartLegend legend={legend} />}
    </StyledBarGroupChartWrapper>
  );
};

BarGroupChart.propTypes = {
  data: PropTypes.array,
  margin: PropTypes.object,
  xValueKey: PropTypes.string,
  xLabelKey: PropTypes.string,
  keys: PropTypes.array,
  colors: PropTypes.array,
  legend: PropTypes.array,
  padding: PropTypes.number,
  emptyDataMessage: PropTypes.string,
  areBarsRounded: PropTypes.bool
};

BarGroupChart.defaultProps = {
  emptyDataMessage: 'No content'
};

export default memo(withTheme(BarGroupChart));
